<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import DropdownSeparator from "@/dsComponents/Dropdown/DropdownSeparator.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

import {
    type AgencyOption,
    FollowingCategoryType,
    FollowingFilterPanel,
    type ProfilPreviewFollowingHeaderProps,
} from "@/components/modal/Profile/previewProfile/types";

const CATEGORY_IDS: FollowingCategoryType[] = [
    FollowingCategoryType.Needs,
    FollowingCategoryType.Missions,
    FollowingCategoryType.Candidatures,
    FollowingCategoryType.Modifications,
    FollowingCategoryType.Contact,
    FollowingCategoryType.Notes,
    FollowingCategoryType.Interviews,
];

const props = withDefaults(defineProps<ProfilPreviewFollowingHeaderProps>(), {
    agencies: () => [],
    selectedAgencyIds: () => [],
    selectedCategoryIds: () => [],
});

const emit = defineEmits<{
    (e: "update:selectedAgencyIds", value: string[]): void;
    (e: "update:selectedCategoryIds", value: FollowingCategoryType[]): void;
}>();

const { t } = useI18n();

const isOpen = ref(false);
const panel = ref<FollowingFilterPanel>(FollowingFilterPanel.Root);
const agencySearchQuery = ref("");

watch(isOpen, (open) => {
    if (!open) {
        panel.value = FollowingFilterPanel.Root;
    }
});

const selectedAgencyIds = computed({
    get: () => props.selectedAgencyIds,
    set: (value) => emit("update:selectedAgencyIds", value),
});

const selectedCategoryIds = computed({
    get: () => props.selectedCategoryIds,
    set: (value) => emit("update:selectedCategoryIds", value),
});

const getAgencyLabel = (agency: AgencyOption) => {
    const fromApi = agency.publicName?.trim();
    if (fromApi) return fromApi;
    if (agency.labelKey) return t(agency.labelKey);
    return agency.id?.trim() ?? "";
};

const filteredAgencies = computed(() => {
    const query = agencySearchQuery.value.toLowerCase().trim();
    if (!query) return props.agencies;
    return props.agencies.filter((agency) => getAgencyLabel(agency).toLowerCase().includes(query));
});

const hasActiveFilters = computed(() => selectedAgencyIds.value.length > 0 || selectedCategoryIds.value.length > 0);

const handleOpenPanel = (target: FollowingFilterPanel) => {
    panel.value = target;
};

const handleToggleAgency = (id: string) => {
    if (!id) return;
    const current = [...selectedAgencyIds.value];
    const index = current.indexOf(id);
    if (index >= 0) {
        current.splice(index, 1);
    } else {
        current.push(id);
    }
    selectedAgencyIds.value = current;
};

const handleToggleCategory = (id: FollowingCategoryType) => {
    const current = [...selectedCategoryIds.value];
    const index = current.indexOf(id);
    if (index >= 0) {
        current.splice(index, 1);
    } else {
        current.push(id);
    }
    selectedCategoryIds.value = current;
};
</script>

<template>
    <div class="profil-preview-following-header">
        <div class="profil-preview-following-header__row">
            <h2 class="profil-preview-following-header__title typo-md-medium">
                {{ t("modals.profilPreview.tabs.following") }}
            </h2>
            <Dropdown :open="isOpen" width="240px" @toggle="(open: boolean) => (isOpen = open)">
                <template #trigger-zone>
                    <ComboButton icon-only="filter" size="sm" variant="ghost" :active="hasActiveFilters" data-name="filter" />
                </template>

                <template v-if="panel === 'agence'" #dropdown-header>
                    <div class="profil-preview-following-header__dropdown-header">
                        <TextInputAlt
                            v-model="agencySearchQuery"
                            :placeholder="t('modals.profilPreview.following.filter.placeholder')"
                            size="xs"
                            focused
                        />
                        <DropdownSeparator />
                    </div>
                </template>

                <template #dropdown-content>
                    <template v-if="panel === 'root'">
                        <DropdownItem
                            icon-left="agency"
                            :label="t('modals.profilPreview.following.filter.agence')"
                            has-submenu
                            stop
                            @click="handleOpenPanel(FollowingFilterPanel.Agence)"
                        />
                        <DropdownItem
                            icon-left="attributes"
                            :label="t('modals.profilPreview.following.filter.categorie')"
                            has-submenu
                            stop
                            @click="handleOpenPanel(FollowingFilterPanel.Categorie)"
                        />
                    </template>
                    <template v-else-if="panel === FollowingFilterPanel.Agence">
                        <DropdownItem
                            v-for="agency in filteredAgencies"
                            :key="agency.id"
                            :label="getAgencyLabel(agency)"
                            :checked="selectedAgencyIds.includes(agency.id)"
                            checkbox
                            stop
                            @select="handleToggleAgency(agency.id)"
                        />
                    </template>
                    <template v-else-if="panel === FollowingFilterPanel.Categorie">
                        <DropdownItem
                            v-for="categoryId in CATEGORY_IDS"
                            :key="categoryId"
                            :label="t(`modals.profilPreview.following.filter.categories.${categoryId}`)"
                            :checked="selectedCategoryIds.includes(categoryId)"
                            checkbox
                            stop
                            @select="handleToggleCategory(categoryId)"
                        />
                    </template>
                </template>
            </Dropdown>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.profil-preview-following-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs, 4px);
    min-height: 32px;
    justify-content: center;

    &__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 32px;
        width: 100%;
    }

    &__title {
        color: var(--neutral-neutral-600, #4c505e);
        margin: 0;
    }

    &__dropdown-header {
        display: flex;
        flex-direction: column;
        gap: 0;
    }
}
</style>
